import { RudderAnalytics } from "@rudderstack/analytics-js";
import { useRouterState } from "@tanstack/react-router";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import type { UserType } from "@/store";

const AnalyticsContext = createContext<{
  rudder?: RudderAnalytics;
}>({});

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

declare global {
  interface Window {
    // biome-ignore lint/suspicious/noExplicitAny: legacy
    posthog: any;
  }
}

export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  const [rudder, setRudder] = useState<RudderAnalytics | undefined>();

  useEffect(() => {
    if (!process.env.RUDDER_DATA_PLANE_URL) {
      return;
    }
    if (!process.env.RUDDER_WRITE_KEY) {
      return;
    }

    const rudderAnalytics = new RudderAnalytics();
    rudderAnalytics.load(
      process.env.RUDDER_WRITE_KEY,
      process.env.RUDDER_DATA_PLANE_URL,
      {
        configUrl: "https://a-api.morty.app",
        destSDKBaseURL: "https://a-cdn.morty.app/v3/modern/js-integrations",
        pluginsSDKBaseURL: "https://a-cdn.morty.app/v3/modern/plugins",
      }
    );

    setRudder(rudderAnalytics);
  }, []);

  return (
    <AnalyticsContext.Provider value={{ rudder }}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export default function useAnalytics() {
  const { rudder } = useContext(AnalyticsContext);

  const page = useCallback(
    // biome-ignore lint/suspicious/noExplicitAny: legacy
    (_category: string, name: string, properties?: Record<string, any>) => {
      rudder?.page(name, properties);
      window.posthog?.capture("$pageview");
    },
    [rudder]
  );

  const track = useCallback(
    // biome-ignore lint/suspicious/noExplicitAny: legacy
    (event: string, properties?: Record<string, any>) => {
      if (window.Intercom) {
        window.Intercom("trackEvent", event, properties);
      }
      rudder?.track(event, properties);
      window.posthog?.capture(event, properties);
    },
    [rudder]
  );

  const identify = useCallback(
    (user: UserType, isPWA: boolean) => {
      const traits = {
        email: user.email,
        is_public: user.isPublic,
        is_pwa: isPWA,
        username: user.username,
      };

      rudder?.identify(user.id, {
        ...traits,
        createdAt: user.dateJoined,
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phoneNumber,
        title: user.pronouns,
        website: user.websiteUrl,
      });
      window.posthog?.identify(user.id, traits, {
        created_at: user.dateJoined,
      });
    },
    [rudder]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignore rudder
  const reset = useCallback(() => {
    if (window.Intercom) {
      window.Intercom("shutdown");
    }
    rudder?.reset();
    window.posthog?.reset();
  }, []);

  return { identify, page, reset, track };
}

export function Pageview() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { page } = useAnalytics();

  // biome-ignore lint/correctness/useExhaustiveDependencies: rerun on pathname change
  useEffect(() => {
    page("Pageview", document.title);
  }, [page, pathname]);

  return null;
}
